import React, { useEffect, useState } from 'react';
import { useUser } from '../../../UserContext';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

// mui
import { Typography, TextField, Button , Avatar, Divider, Autocomplete} from '@mui/material';
import { US_STATES } from '../../../constants';
import { Utils } from '../../../utils/utils';
import { useLocale } from '../../../i18n/LanguageContext';
export default function KitchenSettings(props) {

  const { t } = useLocale();

  const editableTextFieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#ffffff',
    },
  };

  const readOnlyTextFieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#f5f5f5',
    },
  };

  const initialKitchenSettings = {
    name: props.kitchenData.name,
    phoneNumber: props.kitchenData.phoneNumber,
    address: props.kitchenData.address,
    city: props.kitchenData.city,
    state: props.kitchenData.state || US_STATES[0].code,
    zipCode: props.kitchenData.zipCode,
    country: props.kitchenData.country || 'USA',
    profilePic: props.kitchenData.profilePic,
    displayAddress: props.kitchenData.displayAddress
  };
  
  const [kitchenSettings, setKitchenSettings] = useState({
    ...initialKitchenSettings
  });

  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    phoneNumber: '',
    address: '',
    city: '',
    state: '',
    zipCode: ''
  });

  const stripPhoneNumber = (value) => {
    if (!value) return '';
    return value.replace(/\D/g, '');
  };

  const validateField = (name, value) => {
    let error = '';

    if (!value || value.trim() === '') {
      error = t('kitchenSettings.validation.required');
    } else if (name === 'state') {
      if (value.trim().toUpperCase() !== US_STATES[0].code) {
        error = t('kitchenSettings.validation.state', { state: US_STATES[0].code });
      }
    } else if (name === 'zipCode') {
      if (!/^\d+$/.test(value)) {
        error = t('kitchenSettings.validation.zipOnlyNumbers');
      } else if (value.length !== 5) {
        error = t('kitchenSettings.validation.zipLength');
      }
    } else if (name === 'phoneNumber') {
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length < 10) {
        error = t('kitchenSettings.validation.phoneIncomplete');
      } else if (digitsOnly.length > 10) {
        error = t('kitchenSettings.validation.phoneTooLong');
      }
    }

    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setFieldErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error
    }));
  };

  const isFormValid = () => {
    return (
      kitchenSettings.name &&
      kitchenSettings.phoneNumber &&
      kitchenSettings.address &&
      kitchenSettings.city &&
      kitchenSettings.state &&
      kitchenSettings.zipCode &&
      !fieldErrors.name &&
      !fieldErrors.phoneNumber &&
      !fieldErrors.address &&
      !fieldErrors.city &&
      !fieldErrors.state &&
      !fieldErrors.zipCode
    );
  };

  const hasChanges = () => {
    return (
      kitchenSettings.name !== initialKitchenSettings.name ||
      kitchenSettings.phoneNumber !== initialKitchenSettings.phoneNumber ||
      kitchenSettings.address !== initialKitchenSettings.address ||
      kitchenSettings.city !== initialKitchenSettings.city ||
      kitchenSettings.state !== initialKitchenSettings.state ||
      kitchenSettings.zipCode !== initialKitchenSettings.zipCode ||
      kitchenSettings.country !== initialKitchenSettings.country ||
      kitchenSettings.profilePic !== initialKitchenSettings.profilePic ||
      kitchenSettings.displayAddress !== initialKitchenSettings.displayAddress
    );
  };

  function handleKitchenSettingSave() {
    props.onDataChange({...props.kitchenData, 
      name: kitchenSettings.name,
      phoneNumber: kitchenSettings.phoneNumber,
      address: kitchenSettings.address,
      city: kitchenSettings.city,
      state: kitchenSettings.state,
      zipCode: kitchenSettings.zipCode,
      country: kitchenSettings.country,
      profilePic: kitchenSettings.profilePic,
      displayAddress: kitchenSettings.displayAddress,
      addressHasChanged: addressHasChanged()
    });
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    let valueToStore = value;

    if (name === 'state') {
      valueToStore = US_STATES[0].code;
    }
    
    // For zipCode, only allow numeric input and limit to 5 characters
    if (name === 'zipCode') {
      if (value && !/^\d*$/.test(value)) {
        return;
      }
      if (value.length > 5) {
        return;
      }
    }
    
    // For phone number, store plain digits only
    if (name === 'phoneNumber') {
      valueToStore = stripPhoneNumber(value);
    }
    
    setKitchenSettings((prevSettings) => ({
      ...prevSettings,
      [name]: valueToStore,
    }));
  };

  const handleDisplayAddressChange = (e) => {
    const { checked } = e.target;
    setKitchenSettings({...kitchenSettings, displayAddress: checked});
  };

  function addressHasChanged() {
    return (
      kitchenSettings.address !== props.kitchenData.address ||
      kitchenSettings.city !== props.kitchenData.city ||
      kitchenSettings.state !== props.kitchenData.state ||
      kitchenSettings.zipCode !== props.kitchenData.zipCode ||
      kitchenSettings.country !== props.kitchenData.country
    );
  }

  const handleCancel = () => {
    // Reset to initial values from props
    setKitchenSettings({
      ...initialKitchenSettings
    });
    
    // Clear all field errors
    setFieldErrors({
      name: '',
      phoneNumber: '',
      address: '',
      city: '',
      state: '',
      zipCode: ''
    });

    props.onCancel();
  };


  return (
    <div style={{ display: 'flex', maxWidth: 520, flexDirection: 'column'}}>
           
            <form>
            <Typography mt={2} variant="h6">{t('kitchenSettings.nameTitle')}</Typography>
            <div style={{ marginBottom: 16, color: 'gray'}}>{t('kitchenSettings.nameDescription')}</div>

            <TextField
                label={t('kitchenSettings.labelName')}
                name="name"
                value={kitchenSettings.name}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                margin="normal"
                variant="outlined"
                required
                error={!!fieldErrors.name}
                helperText={fieldErrors.name}
                sx={editableTextFieldSx}
                data-testid="kitchen-name-input"
            />

            <TextField
                label={t('kitchenSettings.labelPhone')}
                name="phoneNumber"
                value={Utils.formatPhoneNumber(kitchenSettings.phoneNumber) || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                margin="normal"
                variant="outlined"
                required
                error={!!fieldErrors.phoneNumber}
                helperText={fieldErrors.phoneNumber}
                placeholder="(###) ###-####"
                sx={editableTextFieldSx}
                data-testid="kitchen-phone-input"
            />

            <Typography mt={2} variant="h6">{t('kitchenSettings.locationTitle')}</Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                mb: 2,
                display: 'inline-block',
                px: 1.25,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: '#f3f6ff',
                color: '#44557a',
                border: '1px solid #d9e2f3',
                fontWeight: 500,
                letterSpacing: '0.01em',
              }}
            >
              <b>Note:</b> {t('kitchenSettings.locationNote')}
            </Typography>
            
            <TextField
                label={t('kitchenSettings.labelAddress')}
                name="address"
                value={kitchenSettings.address}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                margin="normal"
                variant="outlined"
                required
                error={!!fieldErrors.address}
                helperText={fieldErrors.address}
                sx={editableTextFieldSx}
                data-testid="kitchen-address-input"
            />

            <TextField
                label={t('kitchenSettings.labelCity')}
                name="city"
                value={kitchenSettings.city}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                margin="normal"
                variant="outlined"
                required
                error={!!fieldErrors.city}
                helperText={fieldErrors.city}
                sx={editableTextFieldSx}
                data-testid="kitchen-city-input"
            />

            <TextField
                label={t('kitchenSettings.labelState')}
                name="state"
                value={kitchenSettings.state || US_STATES[0].code}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                margin="normal"
                variant="outlined"
                required
                InputProps={{
                    readOnly: true,
                }}
                error={!!fieldErrors.state}
                helperText={fieldErrors.state}
                sx={readOnlyTextFieldSx}
                data-testid="kitchen-state-input"
            />

            <TextField
                label={t('kitchenSettings.labelZip')}
                name="zipCode"
                value={kitchenSettings.zipCode}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                margin="normal"
                variant="outlined"
                required
                error={!!fieldErrors.zipCode}
                helperText={fieldErrors.zipCode}
                sx={editableTextFieldSx}
                data-testid="kitchen-zipcode-input"
            />

            <TextField
                label={t('kitchenSettings.labelCountry')}
                name="country"
                value={kitchenSettings.country}
                fullWidth
                margin="normal"
                variant="outlined"
                InputProps={{
                    readOnly: true,
                }}
                sx={readOnlyTextFieldSx}
                data-testid="kitchen-country-input"
            />

                <div style={{ display: 'flex', gap:8, justifyContent: 'right', marginTop: 16}}>
                  <Button style={{ textTransform: 'none'}} type="button" variant="contained" color="inherit" onClick={handleCancel} disabled={!hasChanges()} data-testid="cancel-settings-button">
                        {t('kitchenSettings.cancel')}
                    </Button>
                  <Button style={{ textTransform: 'none'}} variant="contained" color="primary" onClick={handleKitchenSettingSave} disabled={!isFormValid() || !hasChanges()} data-testid="save-settings-button">
                        {t('kitchenSettings.save')}
                    </Button>
                </div>
            </form>
      
    </div>
  );
}